import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import KAccess from './KAccess'
import ServiceCostSubtitle from './ServiceCostSubtitle'
import RenderObject from './KRenderKyc'
import { AmlHit, getAmlHitId, normalizeAml } from './AmlChecks'
import { setAccessToken } from '../helpers'
import { t } from '../locale'
import { AmlMonitoringSubscription, StoredAmlCheck } from '../react-app-env'
import {
  fetchAccess,
  fetchAmlChecks,
  fetchAmlMonitoring,
  fetchUserEmail,
  setAmlMonitoringStatus,
  updateAmlHitStatus,
  validatePinCode,
} from '../service/client'
import { fetchServiceCosts, ServiceCosts } from '../service/subscription'
import { ensureEnoughCredit } from '../helpers/credit.helper'

interface Props {
  clientId: string
  // Extra content (e.g. an "Export PDF" button) rendered next to the
  // "AML checks saved" heading.
  savedActions?: ReactNode
}

// Self-contained AML checks section: the always-visible "saved" / "deleted"
// blocks, per-person delete & restore, and the AML-monitoring toggle (with its
// PIN gate). Shared by the client preview (View) and the Stammdaten preview.
export default function AmlChecksPanel({ clientId, savedActions }: Props) {
  const [amlCheck, setAmlCheck] = useState<StoredAmlCheck[]>([])
  const [amlMonitoring, setAmlMonitoring] = useState<AmlMonitoringSubscription[]>([])
  const [amlMonitoringLoading, setAmlMonitoringLoading] = useState<string | null>(null)
  const [serviceCosts, setServiceCosts] = useState<ServiceCosts | null>(null)
  const [pinAccess, setPinAccess] = useState(false)
  const [pin, setPin] = useState('')
  const [pinRequired, setPinRequired] = useState(true)
  const [accessSubtitle, setAccessSubtitle] = useState<ReactNode>(undefined)
  const pendingMonitoring = useRef<string | null>(null)

  const loadAmlChecks = useCallback(async () => {
    try {
      const response = await fetchAmlChecks(clientId)
      if (response?.ok) setAmlCheck(response.result || [])
    } catch (error) {
      console.error('Error fetching AML checks:', error)
    }
  }, [clientId])

  const loadAmlMonitoring = useCallback(async () => {
    try {
      const response = await fetchAmlMonitoring(clientId)
      if (response?.ok) setAmlMonitoring(response.result || [])
    } catch (error) {
      console.error('Error fetching AML monitoring:', error)
    }
  }, [clientId])

  useEffect(() => {
    if (!clientId) return
    loadAmlChecks()
    loadAmlMonitoring()
  }, [clientId, loadAmlChecks, loadAmlMonitoring])

  useEffect(() => {
    fetchServiceCosts().then(setServiceCosts)
    fetchUserEmail().then(response => {
      if (response?.user?.email) {
        localStorage.setItem('email', response.user.email)
      }
    })
  }, [])

  const amlHitDelete = async (
    amlCheckId: string | undefined,
    hitId: string | null,
    deleted: boolean
  ) => {
    if (!amlCheckId || !hitId) return
    const response = await updateAmlHitStatus(amlCheckId, hitId, deleted)
    response && loadAmlChecks()
  }

  const isAmlMonitoringActive = (amlCheckId?: string) => {
    if (!amlCheckId) return false
    return amlMonitoring.some(
      subscription => subscription.amlCheckId === amlCheckId && subscription.status === 'Active'
    )
  }

  const applyMonitoring = async (amlCheckId: string, enabled: boolean) => {
    // Only enabling costs points; refuse up front when the balance is short.
    if (enabled && !(await ensureEnoughCredit('aml-monitoring'))) return

    try {
      setAmlMonitoringLoading(amlCheckId)
      const response = await setAmlMonitoringStatus(clientId, amlCheckId, enabled)

      if (response?.ok) {
        await loadAmlMonitoring()
      } else if (response?.message?.content === 'notEnoughCredit') {
        alert(t('notEnoughCredit'))
      } else {
        alert('AML monitoring could not be updated.')
      }
    } catch (error) {
      console.error('Error updating AML monitoring:', error)
      alert('AML monitoring could not be updated.')
    } finally {
      setAmlMonitoringLoading(null)
    }
  }

  const getAccess = async () => {
    const result = await fetchAccess(localStorage.getItem('email'))
    if (result?.accessToken) {
      setAccessToken(result.accessToken)
      setPin(result.pinCode)
      setPinRequired(result.pinRequired !== false)
      setPinAccess(true)
    }
  }

  const checkPin = async (pinCode: number) => {
    try {
      const result = await validatePinCode(pinCode)
      if (result?.userAccess) {
        setPinAccess(false)
        const amlCheckId = pendingMonitoring.current
        pendingMonitoring.current = null
        if (amlCheckId) await applyMonitoring(amlCheckId, true)
      }
    } catch (error) {
      console.error('Error validating pin code:', error)
    }
  }

  const handleAmlMonitoringChange = (amlCheckId: string, enabled: boolean) => {
    if (!enabled) {
      applyMonitoring(amlCheckId, false)
      return
    }
    pendingMonitoring.current = amlCheckId
    setAccessSubtitle(
      <ServiceCostSubtitle label='AML Monitoring' points={serviceCosts?.['aml-monitoring']} />
    )
    getAccess()
  }

  const isLatestAmlCheckForSubject = (currentCheck: StoredAmlCheck) => {
    const subjectKey = (check: StoredAmlCheck) =>
      [
        check.clientInfoId ?? '',
        check.clientRepresentId ?? '',
        check.beneficialOwnerType ?? '',
        check.isPerson === false ? 'business' : 'person',
      ].join(':')

    const latestCheck = amlCheck.find(check => subjectKey(check) === subjectKey(currentCheck))
    return latestCheck?._id === currentCheck._id
  }

  const renderAmlGroup = (group: StoredAmlCheck, groupIndex: number, deletedView: boolean) => {
    const hits = Array.isArray(group.items) ? normalizeAml(group.items) : []
    const shownHits = hits.filter(hit => (deletedView ? !!hit.deleted : !hit.deleted))

    // In the "deleted" tab only show checks that actually have a deleted person.
    if (deletedView && shownHits.length === 0) return null
    // In the "saved" tab hide a check whose every person has been deleted
    // (it moves to the other tab); keep genuinely empty "no match" checks.
    if (!deletedView && hits.length > 0 && shownHits.length === 0) return null

    return (
      <div className='border rounded p-3 mb-3' key={group._id || `aml-${groupIndex}`}>
        <div className='d-flex flex-wrap align-items-center gap-2 mb-2'>
          <strong>{group.request?.name || group.query}</strong>
          <span className='text-muted'>
            | {t('date')}: {group.createdAt}
          </span>
          <span className={`badge ${group.status === 'Success' ? 'bg-success' : 'bg-danger'}`}>
            {t(group.status)}
          </span>
        </div>

        {group.status === 'Success' ? (
          <>
            {!deletedView &&
              group._id &&
              hits.length === 0 &&
              isLatestAmlCheckForSubject(group) && (
                <Form.Check
                  type='switch'
                  id={`aml-monitoring-${group._id}`}
                  className='d-flex align-items-center gap-2 mb-3'
                  label='AML Monitoring'
                  checked={isAmlMonitoringActive(group._id)}
                  disabled={amlMonitoringLoading === group._id}
                  onChange={event =>
                    handleAmlMonitoringChange(group._id as string, event.target.checked)
                  }
                />
              )}

            {shownHits.length > 0 ? (
              shownHits.map((item, i) => (
                <AmlHit
                  key={getAmlHitId(item) || i}
                  item={item}
                  onDelete={
                    deletedView ? undefined : () => amlHitDelete(group._id, getAmlHitId(item), true)
                  }
                  onRestore={
                    deletedView
                      ? () => amlHitDelete(group._id, getAmlHitId(item), false)
                      : undefined
                  }
                />
              ))
            ) : (
              <p className='mb-0 text-muted'>{t('noResults')}</p>
            )}
          </>
        ) : (
          <div className='alert alert-danger mb-0'>
            <p className='fw-semibold mb-2'>AML search failed</p>
            {group.response ? (
              <RenderObject data={group.response} />
            ) : (
              <p className='mb-0'>Error details are not available.</p>
            )}
          </div>
        )}
      </div>
    )
  }

  const hasDeletedAmlHits = amlCheck.some(group =>
    (Array.isArray(group.items) ? normalizeAml(group.items) : []).some(hit => !!hit.deleted)
  )

  return (
    <div className='checks-section'>
      <div className='checks-section__title'>
        <i className='bi bi-search' />
        {t('amlChecks')}
      </div>

      <div className='d-flex justify-content-between align-items-center mb-2'>
        <h6 className='mb-0'>{t('amlChecksSaved')}</h6>
        {savedActions}
      </div>
      <div data-pdf-section='aml-saved' className='mb-4'>
        {amlCheck.length > 0 ? (
          amlCheck.map((group, groupIndex) => renderAmlGroup(group, groupIndex, false))
        ) : (
          <p>{t('noResults')}</p>
        )}
      </div>

      <h6 className='mb-2'>{t('amlChecksDeleted')}</h6>
      <div className='mb-4'>
        {hasDeletedAmlHits ? (
          amlCheck.map((group, groupIndex) => renderAmlGroup(group, groupIndex, true))
        ) : (
          <p>{t('noResults')}</p>
        )}
      </div>

      {pinAccess && (
        <KAccess
          show={pinAccess}
          title={pinRequired ? t('pinCodeSent') : t('confirmAction')}
          pin={pin}
          pinRequired={pinRequired}
          subtitle={accessSubtitle}
          onConfirm={(pinCode: number) => checkPin(pinCode)}
          onClose={() => {
            pendingMonitoring.current = null
            setPinAccess(false)
          }}
        />
      )}
    </div>
  )
}
